import { DownloadIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'
import { CSVLink } from 'react-csv'
import { useEffect, useRef, useState } from 'react'
import { PaytrieAPI } from '@paytrie/client'

const REPORT_FETCH_PAGE_SIZE = 100

type Props = {
  firstName: string
}

type KoinlyRow = {
  date: string
  sent_amount?: string
  sent_currency?: string
  received_amount?: string
  received_currency?: string
  txhash?: string | null
  description: string
}

const headers = [
  { label: 'Date', key: 'date' },
  { label: 'Received Amount', key: 'received_amount' },
  { label: 'Received Currency', key: 'received_currency' },
  { label: 'Sent Amount', key: 'sent_amount' },
  { label: 'Sent Currency', key: 'sent_currency' },
  { label: 'Description', key: 'description' },
  { label: 'TxHash', key: 'txhash' },
]

const isValidDate = (date: any) => {
  return !isNaN(Date.parse(date))
}

const formatNumber = (value: any, decimals: number = 2) => {
  return typeof value === 'number' ? value.toFixed(decimals) : '0.00'
}

const buildKoinlyRows = (transactions: PaytrieAPI.Transaction[]): KoinlyRow[] =>
  transactions
    .filter((t) => t.status === 'complete')
    .map((t) => {
      const baseDate =
        t.date && isValidDate(t.date) ? new Date(t.date) : new Date()

      const incrementedDate = (seconds: number) =>
        new Date(baseDate.getTime() + seconds * 1000).toLocaleString('en-CA', {
          dateStyle: 'short',
          timeStyle: 'long',
          hourCycle: 'h23',
          timeZone: 'UTC',
        })

      const isBuy = t.leftSideLabel === 'CAD'
      const label = isBuy ? 'Buy Stablecoin' : 'Sell Stablecoin'

      const transferRow = {
        date: isBuy ? incrementedDate(1) : incrementedDate(-1),
        sent_amount: isBuy ? formatNumber(t.rightSideValue) : undefined,
        sent_currency: isBuy ? t.rightSideLabel.split('-')[0] : undefined,
        received_amount: !isBuy ? formatNumber(t.leftSideValue) : undefined,
        received_currency: !isBuy ? t.leftSideLabel.split('-')[0] : undefined,
        txhash: t.paymentId || null,
        description: isBuy ? 'Transfer' : 'Deposit',
      }

      const mainRow = {
        date: incrementedDate(0),
        sent_amount: formatNumber(t.leftSideValue),
        sent_currency: t.leftSideLabel.split('-')[0],
        received_amount: formatNumber(t.rightSideValue),
        received_currency: t.rightSideLabel.split('-')[0],
        description: label,
      }

      const depositOrWithdrawRow = {
        date: isBuy ? incrementedDate(-1) : incrementedDate(1),
        received_amount: isBuy ? formatNumber(t.leftSideValue) : undefined,
        received_currency: isBuy ? t.leftSideLabel.split('-')[0] : undefined,
        sent_amount: !isBuy ? formatNumber(t.rightSideValue) : undefined,
        sent_currency: !isBuy ? t.rightSideLabel.split('-')[0] : undefined,
        description: isBuy ? 'Deposit' : 'Withdraw',
      }

      return [transferRow, mainRow, depositOrWithdrawRow]
    })
    .flat()

const fetchAllTransactions = async (): Promise<PaytrieAPI.Transaction[]> => {
  const all: PaytrieAPI.Transaction[] = []
  let offset = 0

  for (;;) {
    const response = await PaytrieAPI.listTransactions({
      limit: REPORT_FETCH_PAGE_SIZE,
      offset,
    })
    if (response.status !== 200) break

    const { items, total } = response.data.data
    all.push(...items)
    offset += REPORT_FETCH_PAGE_SIZE

    if (all.length >= total || items.length === 0) break
  }

  return all
}

export const ReportButton = ({ firstName }: Props) => {
  const { t } = useTranslation()
  const [rows, setRows] = useState<KoinlyRow[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const csvContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (rows.length > 0) {
      csvContainerRef.current?.querySelector('a')?.click()
      setRows([])
    }
  }, [rows])

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const allTransactions = await fetchAllTransactions()
      setRows(buildKoinlyRows(allTransactions))
    } catch {
      console.warn('Unable to export transactions')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      <button
        onClick={handleExport}
        disabled={isExporting}
        className="px-4 py-2 bg-black text-white dark:bg-white dark:text-black flex items-center disabled:opacity-50"
        data-testid="btn-report-export"
      >
        <span data-testid="text-report-export-label">
          {t('reportButton.exportCsv')}
        </span>
        <DownloadIcon
          className="w-4 h-4 ml-2"
          data-testid="icon-report-download"
        />
      </button>
      <div ref={csvContainerRef} className="hidden">
        <CSVLink
          filename={`${firstName}_paytrie_koinly.csv`}
          data={rows}
          headers={headers}
          data-testid="link-report-csv"
        />
      </div>
    </>
  )
}
